import { call, put, takeLatest, delay } from 'redux-saga/effects';
import {
    fetchCapabilitiesStart,
    fetchCapabilitiesSuccess,
    fetchCapabilitiesFailure,
    fetchCapabilitiesRequest,
} from '../capabilitiesSlice';
import { getCapabilitiesApi } from '../../api/apiLists/getCapabilitiesApi';
import { callWithAuth } from '../sagaApiHelper';
import { getCapabilitiesFromStorage, saveCapabilitiesToStorage } from '../../utils/capabilitiesStorage';
import { hasActivePlan, getPlanIds } from '../../utils/capabilitiesHelper';
import { normalizeCapabilities } from '../../utils/capabilitiesNormalizer';
import { fetchAuthSession } from '@aws-amplify/auth';

const normalizeCapabilitiesResponse = (data) => (
    data?.context ? data : {
        isAuthorized: data?.isAuthorized || false,
        context: data
    }
);

const isCapabilitiesPollComplete = (capabilities, { pollUntil = 'authorized', expectedPlanId } = {}) => {
    if (!capabilities) {
        return false;
    }

    const normalized = normalizeCapabilities(capabilities);

    if (pollUntil === 'activePlan') {
        if (!hasActivePlan(normalized)) {
            return false;
        }
        if (expectedPlanId) {
            return getPlanIds(normalized).includes(expectedPlanId);
        }
        return true;
    }

    return normalized.isAuthorized === true;
};

function* handleCapabilitiesPolling(pollOptions = {}) {
    const maxRetries = 10;
    const pollIntervalMs = 3000;
    let capabilities = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            const res = yield call(callWithAuth, getCapabilitiesApi, []);

            if (res.status) {
                capabilities = normalizeCapabilitiesResponse(res.data);

                if (isCapabilitiesPollComplete(capabilities, pollOptions)) {
                    break;
                }

                if (attempt < maxRetries) {
                    yield delay(pollIntervalMs);
                }
            } else if (attempt < maxRetries) {
                yield delay(pollIntervalMs);
            }
        } catch (pollError) {
            if (pollError?.name === 'HTTPUnauthorized' && attempt < maxRetries) {
                console.warn('Unauthorized during capabilities polling, retrying...');
                yield delay(pollIntervalMs);
            } else if (attempt < maxRetries) {
                yield delay(pollIntervalMs);
            }
        }
    }

    return capabilities;
}

function* handleFetchCapabilities(action) {
    try {
        const shouldPoll = action.payload?.shouldPoll || false;
        const pollUntil = action.payload?.pollUntil || 'authorized';
        const expectedPlanId = action.payload?.expectedPlanId;
        const silent = action.payload?.silent || false;
        const pollOptions = { pollUntil, expectedPlanId };

        if (!shouldPoll) {
            const cachedCapabilities = getCapabilitiesFromStorage();
            if (cachedCapabilities) {
                yield put(fetchCapabilitiesSuccess(cachedCapabilities));
                return;
            }
        }

        if (!silent) {
            yield put(fetchCapabilitiesStart());
        }
        
        let capabilities = null;
        let retryCount = 0;
        const maxRetries = 2;

        while (retryCount < maxRetries) {
            try {
                if (shouldPoll) {
                    capabilities = yield call(handleCapabilitiesPolling, pollOptions);
                    if (capabilities) {
                        break;
                    }
                    retryCount += 1;
                } else {
                    const res = yield call(callWithAuth, getCapabilitiesApi, []);

                    if (res.status) {
                        capabilities = normalizeCapabilitiesResponse(res.data);
                        break;
                    } else {
                        yield put(fetchCapabilitiesFailure(res.errMsg || 'Failed to fetch capabilities'));
                        return;
                    }
                }
            } catch (error) {
                if (error?.name === 'HTTPUnauthorized' && retryCount < maxRetries - 1) {
                    console.warn('Received 401, refreshing token and retrying...');
                    retryCount++;
                    try {
                        yield call([fetchAuthSession, fetchAuthSession], { forceRefresh: true });
                    } catch (refreshError) {
                        console.error('Failed to refresh token:', refreshError);
                        yield put(fetchCapabilitiesFailure('Authentication failed. Please login again.'));
                        return;
                    }
                } else if (retryCount >= maxRetries - 1) {
                    throw error;
                } else {
                    throw error;
                }
            }
        }
        
        if (capabilities) {
            yield put(fetchCapabilitiesSuccess(capabilities));
            saveCapabilitiesToStorage(capabilities);
        } else {
            yield put(fetchCapabilitiesFailure('Failed to fetch capabilities'));
        }
    } catch (error) {
        console.error('Error in fetchCapabilities:', error);
        yield put(fetchCapabilitiesFailure(error.message || 'An error occurred while fetching capabilities'));
    }
}

function* capabilitiesSaga() {
    yield takeLatest(fetchCapabilitiesRequest.type, handleFetchCapabilities);
}

export default capabilitiesSaga;
